// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  stats.js — utility statistiche pure, no dipendenze.                      ║
// ║                                                                            ║
// ║  Proposte come complemento a engine.stats.* in src/App.jsx.                ║
// ║  Tutte funzioni pure (no side effects), testabili in isolamento.          ║
// ║                                                                            ║
// ║  Reference: vedi docs/STATS_AUDIT_2026-05-19.md per giustificazione.       ║
// ╚══════════════════════════════════════════════════════════════════════════╝

// ─── Primitive di base ───────────────────────────────────────────────────────

/**
 * Quantile tipo-7 (R default) con linear interpolation.
 * Per p=0.5 produce la mediana corretta (interpolata per n pari).
 *
 * @param {number[]} sortedAsc — array ordinato crescente
 * @param {number} p — probabilità in [0,1]
 * @returns {number}
 */
export function quantInterp(sortedAsc, p) {
  if (!sortedAsc || sortedAsc.length === 0) return 0;
  const L = sortedAsc.length;
  if (L === 1) return sortedAsc[0];
  if (p <= 0) return sortedAsc[0];
  if (p >= 1) return sortedAsc[L - 1];
  const h = (L - 1) * p;
  const lo = Math.floor(h);
  const hi = Math.min(L - 1, lo + 1);
  const frac = h - lo;
  return sortedAsc[lo] * (1 - frac) + sortedAsc[hi] * frac;
}
// test: quantInterp([1,2,3,4], 0.5) === 2.5 (mediana pari)
// test: quantInterp([1,2,3,4,5], 0.5) === 3

/**
 * CDF normale standard — Abramowitz & Stegun 26.2.17 (errore < 7.5e-8).
 * Replicata qui per indipendenza da App.jsx.
 */
export function normalCDF(z) {
  if (!Number.isFinite(z)) return 0.5;
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp(-z * z / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return z > 0 ? 1 - p : p;
}

/**
 * Inversa della CDF normale (probit). Beasley-Springer-Moro approssimazione.
 * @param {number} p — probabilità in (0,1)
 * @returns {number} z tale che Φ(z) = p
 */
export function normalQuantile(p) {
  if (p <= 0 || p >= 1) return p <= 0 ? -Infinity : Infinity;
  const a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
  const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
  const d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];
  const pLow = 0.02425, pHigh = 1 - pLow;
  let q, r;
  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  } else if (p <= pHigh) {
    q = p - 0.5; r = q * q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q / (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  }
}
// test: Math.abs(normalQuantile(0.975) - 1.96) < 1e-3

/**
 * CDF Student-t a `df` gradi di libertà.
 * Approssimazione via Abramowitz-Stegun 26.7.4 (series expansion).
 * Sufficiente accuracy per df>3 e |t|<10.
 *
 * @param {number} t — statistica t
 * @param {number} df — gradi di libertà
 * @returns {number} P(T ≤ t)
 */
export function studentTCDF(t, df) {
  if (!Number.isFinite(t) || df < 1) return 0.5;
  // Per df grande, t→z (approssimazione normale)
  if (df >= 100) return normalCDF(t);
  // Trasformazione regularized incomplete beta — approssimazione Hill (1970)
  const x = df / (df + t * t);
  const a = df / 2, b = 0.5;
  const ibeta = regularizedIncompleteBeta(x, a, b);
  // P(T ≤ t) = 1 - 0.5 × ibeta  per t > 0
  const tail = 0.5 * ibeta;
  return t >= 0 ? 1 - tail : tail;
}
// test: Math.abs(studentTCDF(1.96, 1000) - 0.975) < 0.01

// Helper: log-gamma via Lanczos
function logGamma(x) {
  const g = 7;
  const c = [0.99999999999980993, 676.5203681218851, -1259.1392167224028,
             771.32342877765313, -176.61502916214059, 12.507343278686905,
             -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7];
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
  x -= 1;
  let a = c[0];
  const t = x + g + 0.5;
  for (let i = 1; i < g + 2; i++) a += c[i] / (x + i);
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a);
}

// Helper: regularized incomplete beta function I_x(a,b) — Numerical Recipes
function regularizedIncompleteBeta(x, a, b) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bt = Math.exp(logGamma(a + b) - logGamma(a) - logGamma(b)
                    + a * Math.log(x) + b * Math.log(1 - x));
  if (x < (a + 1) / (a + b + 2)) {
    return bt * betaCF(x, a, b) / a;
  } else {
    return 1 - bt * betaCF(1 - x, b, a) / b;
  }
}

function betaCF(x, a, b) {
  const MAXIT = 200, EPS = 3e-7, FPMIN = 1e-30;
  const qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - qab * x / qap;
  if (Math.abs(d) < FPMIN) d = FPMIN;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= MAXIT; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < EPS) return h;
  }
  return h;
}

// ─── Test statistici per A/B menu ─────────────────────────────────────────────

/**
 * Paired t-test (campioni accoppiati).
 *
 * Usa: prima/dopo cambio prezzo, controllando weekday (pareggia martedì pre con
 * martedì post). H0: media delle differenze = 0.
 *
 * @param {number[]} before
 * @param {number[]} after
 * @returns {{ meanDiff, sdDiff, tStat, df, pValue, n, ci95, significant }}
 *
 * Fallback: n<3 → fallback:true.
 */
export function pairedTTest(before, after) {
  if (!Array.isArray(before) || !Array.isArray(after)) {
    return { fallback: true, reason: "input non valido" };
  }
  if (before.length !== after.length) {
    return { fallback: true, reason: "lunghezze diverse" };
  }
  const diffs = [];
  for (let i = 0; i < before.length; i++) {
    const a = Number(before[i]), b = Number(after[i]);
    if (Number.isFinite(a) && Number.isFinite(b)) diffs.push(b - a);
  }
  const n = diffs.length;
  if (n < 3) {
    return { fallback: true, n, reason: `n=${n} troppo piccolo per t-test (servono ≥3)` };
  }
  const mean = diffs.reduce((s, v) => s + v, 0) / n;
  const variance = diffs.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1);
  const sd = Math.sqrt(variance);
  const se = sd / Math.sqrt(n);
  const df = n - 1;
  // Caso degenere: sd=0 (differenze perfettamente costanti).
  // Se mean≠0 → certezza assoluta, t→∞, p≈0. Esprimerlo onestamente.
  if (se === 0) {
    if (mean === 0) {
      return { meanDiff: 0, sdDiff: 0, tStat: 0, df, pValue: 1, n,
               ci95: [0, 0], significant: false, fallback: false, degenerate: "zero variance, zero diff" };
    }
    return { meanDiff: mean, sdDiff: 0, tStat: Infinity, df, pValue: 0, n,
             ci95: [mean, mean], significant: true, fallback: false,
             degenerate: "zero variance, nonzero diff (certezza assoluta)" };
  }
  const tStat = mean / se;
  // p-value bilaterale
  const pValue = 2 * (1 - studentTCDF(Math.abs(tStat), df));
  // CI 95% sulla media delle differenze (Student t critico)
  const tCrit = approxStudentTCrit(df, 0.025);
  const ciHalf = tCrit * se;
  return {
    meanDiff: mean,
    sdDiff: sd,
    tStat, df, pValue,
    n,
    ci95: [mean - ciHalf, mean + ciHalf],
    significant: pValue < 0.05,
    fallback: false,
  };
}
// test: pairedTTest([1,2,3,4,5], [1,2,3,4,5]).pValue ≈ 1 (identici)
// test: pairedTTest([1,2,3], [3,4,5]).significant === true (sempre +2)

function approxStudentTCrit(df, alpha) {
  // Newton sull'inverso della CDF Student-t
  const target = 1 - alpha;
  let t = normalQuantile(target); // start from normal approx
  for (let i = 0; i < 30; i++) {
    const cdf = studentTCDF(t, df);
    const pdf = studentTPDF(t, df);
    if (pdf < 1e-12) break;
    const delta = (cdf - target) / pdf;
    t -= delta;
    if (Math.abs(delta) < 1e-6) break;
  }
  return t;
}

function studentTPDF(t, df) {
  const logPdf = logGamma((df + 1) / 2) - logGamma(df / 2)
               - 0.5 * Math.log(df * Math.PI)
               - ((df + 1) / 2) * Math.log(1 + t * t / df);
  return Math.exp(logPdf);
}

/**
 * Mann-Whitney U test (rank-sum, non-parametrico).
 *
 * Robusto a distribuzioni asimmetriche tipiche del ticket F&B (log-normale).
 * H0: le due popolazioni hanno la stessa distribuzione.
 * Approssimazione normale per n1+n2 ≥ 20 (Mann-Whitney 1947).
 *
 * @param {number[]} sampleA
 * @param {number[]} sampleB
 * @returns {{ U, z, pValue, n1, n2, significant, fallback }}
 */
export function mannWhitneyU(sampleA, sampleB) {
  if (!Array.isArray(sampleA) || !Array.isArray(sampleB)) {
    return { fallback: true, reason: "input non valido" };
  }
  const a = sampleA.filter(v => Number.isFinite(v));
  const b = sampleB.filter(v => Number.isFinite(v));
  const n1 = a.length, n2 = b.length;
  if (n1 < 5 || n2 < 5) {
    return { fallback: true, n1, n2, reason: `campioni piccoli (n1=${n1}, n2=${n2}, servono ≥5 ciascuno)` };
  }
  // Combina + rank con tie-correction
  const combined = a.map(v => ({ v, g: "a" })).concat(b.map(v => ({ v, g: "b" })));
  combined.sort((x, y) => x.v - y.v);
  // Assegna rank medi su tie
  let i = 0;
  while (i < combined.length) {
    let j = i;
    while (j + 1 < combined.length && combined[j + 1].v === combined[i].v) j++;
    const avgRank = (i + j) / 2 + 1; // rank 1-indexed
    for (let k = i; k <= j; k++) combined[k].rank = avgRank;
    i = j + 1;
  }
  const R1 = combined.filter(x => x.g === "a").reduce((s, x) => s + x.rank, 0);
  const U1 = R1 - n1 * (n1 + 1) / 2;
  const U2 = n1 * n2 - U1;
  const U = Math.min(U1, U2);
  // Normal approximation con tie-correction
  const meanU = n1 * n2 / 2;
  // Tie correction sigma
  const tieGroups = new Map();
  for (const x of combined) tieGroups.set(x.v, (tieGroups.get(x.v) || 0) + 1);
  let tieSum = 0;
  for (const t of tieGroups.values()) if (t > 1) tieSum += t * t * t - t;
  const N = n1 + n2;
  const sigmaU = Math.sqrt((n1 * n2 / 12) * ((N + 1) - tieSum / (N * (N - 1))));
  const z = sigmaU > 0 ? (U - meanU) / sigmaU : 0;
  const pValue = 2 * (1 - normalCDF(Math.abs(z)));
  return {
    U, U1, U2, z, pValue, n1, n2,
    significant: pValue < 0.05,
    fallback: false,
  };
}
// test: mannWhitneyU([1,2,3,4,5], [1,2,3,4,5]).pValue ≈ 1
// test: mannWhitneyU([1,2,3,4,5], [6,7,8,9,10]).pValue < 0.05

// ─── Power analysis & sample size ────────────────────────────────────────────

/**
 * Sample size minimo per detect Δ con potenza 1−β e livello α (bilaterale).
 * Formula: n* = 2 × (z_{α/2} + z_β)² × σ² / Δ²
 *
 * Conservativa: assume due campioni con stessa σ.
 *
 * @param {number} delta — effetto minimo da detect (in unità della variabile)
 * @param {number} sigma — deviazione standard stimata
 * @param {number} alpha — livello (default 0.05)
 * @param {number} power — 1 − β (default 0.80)
 * @returns {number} n minimo per gruppo
 */
export function sampleSize(delta, sigma, alpha = 0.05, power = 0.80) {
  if (delta <= 0 || sigma <= 0) return Infinity;
  const zA = normalQuantile(1 - alpha / 2);
  const zB = normalQuantile(power);
  const n = 2 * Math.pow(zA + zB, 2) * Math.pow(sigma, 2) / Math.pow(delta, 2);
  return Math.ceil(n);
}
// test: sampleSize(2, 4) ≈ 63 (esempio carbonara dal system prompt)

/**
 * "Quanti dati in più mi servono per dimezzare la banda CI?"
 * Approssimazione Wald: la larghezza CI scala come 1/√n.
 *
 * @param {number} nNow — osservazioni attuali
 * @param {number} ciHalfNow — larghezza metà CI attuale
 * @param {number} ciHalfTarget — larghezza metà CI desiderata
 * @returns {number} n* totale (NON aggiuntivo; sottrai nNow per il delta)
 */
export function requiredSampleSize(nNow, ciHalfNow, ciHalfTarget) {
  if (nNow <= 0 || ciHalfNow <= 0 || ciHalfTarget <= 0) return Infinity;
  if (ciHalfTarget >= ciHalfNow) return nNow;
  return Math.ceil(nNow * Math.pow(ciHalfNow / ciHalfTarget, 2));
}
// test: requiredSampleSize(30, 4, 2) === 120 (4× per dimezzare banda)

// ─── Forecast: Holt-Winters triplo ───────────────────────────────────────────

/**
 * Holt-Winters triplo moltiplicativo (livello + trend + stagionalità).
 *
 * Workhorse F&B per stagionalità weekday/mensile. Usare con dataPoints ≥ 60
 * (almeno 2 cicli completi del period). Sotto, restare su baseline weekday.
 *
 * @param {number[]} series — serie cronologica
 * @param {object} opts
 *   - period: 7 (weekday) | 30 (mensile)
 *   - alpha, beta, gamma: smoothing parameters (default 0.3, 0.1, 0.2)
 *   - horizon: numero di step da predire
 * @returns {{ fitted, forecast:[{point,lower,upper}], sigma, params, mape, fallback }}
 *
 * MAPE > 25% → suggerire fallback al modello semplice (modello non fitta bene).
 *
 * Reference: Holt (1957), Winters (1960), Hyndman & Athanasopoulos
 * "Forecasting: Principles and Practice" (3rd ed.), § 8.3.
 */
export function holtWinters(series, opts = {}) {
  const { period = 7, alpha = 0.3, beta = 0.1, gamma = 0.2, horizon = 14 } = opts;
  if (!Array.isArray(series) || series.length < 2 * period) {
    return { fallback: true, reason: `servono ≥${2 * period} osservazioni (attuali: ${series?.length || 0})` };
  }
  const data = series.map(v => Number(v) || 0);
  const n = data.length;
  // Inizializzazione: livello L = media primo ciclo, trend T = (medio2°ciclo − medio1°ciclo)/period
  let L = data.slice(0, period).reduce((a, b) => a + b, 0) / period;
  const L2 = data.slice(period, 2 * period).reduce((a, b) => a + b, 0) / period;
  let T = (L2 - L) / period;
  const S = new Array(period);
  for (let i = 0; i < period; i++) S[i] = L > 0 ? data[i] / L : 1;
  // Smoothing
  const fitted = [];
  for (let t = 0; t < n; t++) {
    const idx = t % period;
    const Lprev = L;
    const sIdx = S[idx] > 0 ? S[idx] : 1;
    L = alpha * (data[t] / sIdx) + (1 - alpha) * (L + T);
    T = beta * (L - Lprev) + (1 - beta) * T;
    S[idx] = gamma * (data[t] / (L > 0 ? L : 1)) + (1 - gamma) * sIdx;
    fitted.push((Lprev + T) * sIdx);
  }
  // Residui → sigma stimata
  const residuals = [];
  for (let t = period; t < n; t++) residuals.push(data[t] - fitted[t]);
  const sigma = residuals.length > 1
    ? Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / (residuals.length - 1))
    : 0;
  // MAPE per diagnostica fit
  let mapeSum = 0, mapeN = 0;
  for (let t = period; t < n; t++) {
    if (data[t] > 0) { mapeSum += Math.abs(residuals[t - period]) / data[t]; mapeN++; }
  }
  const mape = mapeN > 0 ? mapeSum / mapeN : NaN;
  // Forecast con banda √h (random walk)
  const forecast = [];
  for (let h = 1; h <= horizon; h++) {
    const idx = (n - 1 + h) % period;
    const point = (L + h * T) * S[idx];
    const ciHalf = 1.96 * sigma * Math.sqrt(h);
    forecast.push({
      step: h,
      point: Number.isFinite(point) ? point : 0,
      lower: Math.max(0, point - ciHalf),
      upper: point + ciHalf,
      ciHalf,
    });
  }
  return {
    fitted, forecast, sigma, mape,
    params: { alpha, beta, gamma, period },
    fallback: false,
    suggestFallbackToSimple: Number.isFinite(mape) && mape > 0.25,
  };
}
// test: const r = holtWinters([100,80,90,110,150,200,180].concat([105,82,93,113,153,205,185]), { period:7, horizon:7 });
//       r.fallback === false && r.forecast.length === 7

// ─── Multi-change-point CUSUM ────────────────────────────────────────────────

/**
 * Recursive CUSUM: trova tutti i change-point in una serie.
 *
 * Applica CUSUM ricorsivamente sulle partizioni. Stop quando segmento < minSeg
 * o magnitudine sotto soglia.
 *
 * @param {number[]} series
 * @param {object} opts
 *   - minSeg: lunghezza minima segmento (default 14gg)
 *   - thresholdSigma: soglia in deviazioni standard (default 2 — Page suggerisce
 *     3 ma per F&B con drift grandi la varianza post-change inflates la sigma
 *     stimata e maschera il segnale; 2 è più adatto a use case pratici).
 *   - maxPoints: limite hard sui change-point trovati (default 5)
 *
 * @returns {{ changePoints:[{at, magnitude, beforeMean, afterMean, deltaPct}],
 *             fallback }}
 *
 * NOTA TEORICA: il CUSUM stima sigma sull'intero segmento, quindi un drift
 * grande aumenta sigma e può auto-mascherarsi. Per detection ad alta sensibilità
 * con drift noti, considerare metodi sequenziali (Page CUSUM unilaterale) o
 * stima della varianza pre-change con finestra mobile.
 */
export function recursiveCUSUM(series, opts = {}) {
  const { minSeg = 14, thresholdSigma = 2, maxPoints = 5 } = opts;
  if (!Array.isArray(series) || series.length < 2 * minSeg) {
    return { changePoints: [], fallback: true };
  }
  const data = series.map(v => Number(v) || 0);
  const result = [];

  function recurse(startIdx, endIdx) {
    if (result.length >= maxPoints) return;
    const seg = data.slice(startIdx, endIdx);
    const n = seg.length;
    if (n < 2 * minSeg) return;
    const mean = seg.reduce((a, b) => a + b, 0) / n;
    const variance = n > 1 ? seg.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1) : 0;
    const sigma = Math.sqrt(variance);
    if (sigma <= 0) return;
    let s = 0, maxAbs = 0, idx = -1;
    for (let i = 0; i < n; i++) {
      s += seg[i] - mean;
      if (Math.abs(s) > maxAbs && i >= minSeg && (n - i) >= minSeg) {
        maxAbs = Math.abs(s);
        idx = i;
      }
    }
    const threshold = thresholdSigma * sigma * Math.sqrt(n);
    if (idx > 0 && maxAbs > threshold) {
      const before = seg.slice(0, idx + 1);
      const after = seg.slice(idx + 1);
      const beforeMean = before.reduce((a, b) => a + b, 0) / before.length;
      const afterMean = after.reduce((a, b) => a + b, 0) / after.length;
      result.push({
        at: startIdx + idx,
        magnitude: maxAbs,
        beforeMean, afterMean,
        deltaPct: beforeMean !== 0 ? (afterMean - beforeMean) / Math.abs(beforeMean) : 0,
      });
      recurse(startIdx, startIdx + idx);
      recurse(startIdx + idx + 1, endIdx);
    }
  }

  recurse(0, data.length);
  result.sort((a, b) => a.at - b.at);
  return { changePoints: result, fallback: false };
}
// test: serie con un drift evidente
//   recursiveCUSUM([10,10,10,10,10,10,10,10,10,10,10,10,10,10,
//                   20,20,20,20,20,20,20,20,20,20,20,20,20,20])
//   .changePoints.length === 1

// ─── Forecast asimmetrico log-normale ────────────────────────────────────────

/**
 * Quantile log-normale: per banda asimmetrica del forecast quando la serie
 * non è normale (ticket €/coperto è log-normale F&B).
 *
 * Trasforma: media e sigma in log-space, applica quantile normale, esponenzia.
 *
 * @param {number} mean — media stimata (linear scale)
 * @param {number} sigma — deviazione standard (linear scale)
 * @param {number} p — probabilità in (0,1)
 * @returns {number} quantile in linear scale
 */
export function logNormalQuantile(mean, sigma, p) {
  if (mean <= 0 || sigma < 0) return mean;
  // Method of moments: log-mean μ e log-sigma σ_log dalla media e σ linear
  const cv2 = (sigma / mean) ** 2;
  const sigmaLog = Math.sqrt(Math.log(1 + cv2));
  const muLog = Math.log(mean) - sigmaLog ** 2 / 2;
  const z = normalQuantile(p);
  return Math.exp(muLog + sigmaLog * z);
}
// test: logNormalQuantile(100, 30, 0.5) < 100 (mediana < media in log-normale)
// test: logNormalQuantile(100, 30, 0.975) > 100 + 1.96 × 30 (asimmetria positiva)

// ─── Autocorrelation lag-1 (per correzione banda quadratura) ─────────────────

/**
 * Coefficiente di autocorrelazione lag-1 (Pearson).
 *
 * Misura: quanto il valore di oggi predice quello di domani. Su serie F&B
 * weekday tipicamente ρ > 0 (lunedì-mercoledì-venerdì sono "feriali" simili,
 * sabato-domenica simili tra loro).
 *
 * Usare per correggere banda CI in `forecastNDays`:
 *   var_totale = σ² × [n + 2 × Σ(n-k) × ρ^k]   (Bartlett)
 * Approssimazione lag-1 only: σ_tot ≈ σ × √(n × (1 + ρ) / (1 − ρ))
 *
 * @param {number[]} series
 * @returns {number} ρ in [-1, 1], 0 se serie troppo corta
 */
export function lag1Autocorrelation(series) {
  if (!Array.isArray(series) || series.length < 3) return 0;
  const data = series.map(v => Number(v) || 0);
  const n = data.length;
  const mean = data.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) den += (data[i] - mean) ** 2;
  if (den <= 0) return 0;
  for (let i = 0; i < n - 1; i++) num += (data[i] - mean) * (data[i + 1] - mean);
  return num / den;
}
// test: serie random → ρ ≈ 0
// test: serie [1,2,1,2,1,2] alternata → ρ < 0
// test: serie [1,2,3,4,5] crescente → ρ > 0.5

// ─── Trend weekday-adjusted (no confusione con stagionalità) ─────────────────

/**
 * Trend weekday-adjusted via regressione lineare sul residuo dopo rimozione
 * dell'effetto giorno-della-settimana.
 *
 * FIX A.3 (audit 2026-05-20): la vecchia `weeklyTrend` confrontava semplicemente
 * sum(last7) vs sum(prev7) — su 14 giorni a cavallo di maggio→giugno (costa
 * adriatica, ingresso piena stagione) il segnale è quasi puramente stagionale
 * weekday × month, NON trend reale. Per un ristorante che passa da bassa stagione
 * a stagione turistica, il vecchio metodo restituiva +30% "trend" che in realtà
 * era solo "il sabato della seconda settimana è più pieno del lunedì della prima".
 *
 * Algoritmo (3 step, OLS classico — nessuna magia):
 *   1. Media per giorno-della-settimana (`baseDow[d]`) sull'intera serie
 *      → cattura l'effetto multiplicativo "sabato vale 2× lunedì".
 *   2. Detrend additivo-relativo: residuo[i] = revenue[i] / baseDow[dow(i)]
 *      → ottieni una serie "normalizzata" intorno a 1.0 dove l'effetto
 *      weekday è stato rimosso.
 *   3. Regressione lineare sui residui: slope = giorni-dall'inizio → residuo.
 *      slope > 0 = trend positivo, slope=0 = solo stagionalità.
 *
 * Output: slope giornaliera in unità relative (es. +0.003 = +0.3%/giorno
 * → +2.1%/settimana al netto della stagionalità weekday).
 *
 * Reference: Hyndman & Athanasopoulos, "Forecasting: Principles and Practice"
 * (3rd ed.), § 3.4 Classical decomposition; § 5.2 Linear regression.
 *
 * Validation requirements:
 *   - n ≥ 28 (4 settimane piene). Sotto: valid=false, non restituire numero.
 *   - Almeno 1 osservazione per ogni dow visto (altrimenti residuo non definito).
 *   - Varianza residui > 0 (altrimenti slope indeterminato).
 *
 * @param {Array<{date: string, revenue: number}>} series — serie ordinata crescente per data
 * @returns {{ slopeWeekly: number, slopeDaily: number, n: number, nUsed: number,
 *             rSquared: number, baseDow: Object<number,number>,
 *             valid: boolean, reason?: string }}
 */
export function weekdayAdjustedTrend(series) {
  if (!Array.isArray(series) || series.length < 28) {
    return {
      slopeWeekly: 0, slopeDaily: 0, n: series?.length || 0, nUsed: 0,
      rSquared: 0, baseDow: {}, valid: false,
      reason: `n=${series?.length || 0} < 28 (servono ≥4 settimane piene per separare trend da stagionalità weekday)`,
    };
  }
  // 1. baseDow: media per giorno della settimana sull'intera serie
  const byDow = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  const sorted = [...series].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  for (const p of sorted) {
    const rev = Number(p.revenue);
    if (!Number.isFinite(rev) || rev <= 0) continue;
    const d = new Date(String(p.date) + "T12:00:00");
    const dow = d.getDay();
    byDow[dow].push(rev);
  }
  const baseDow = {};
  for (let d = 0; d < 7; d++) {
    if (byDow[d].length > 0) baseDow[d] = byDow[d].reduce((s, v) => s + v, 0) / byDow[d].length;
  }
  // 2. detrend → residui relativi
  const xs = [], ys = [];
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i];
    const rev = Number(p.revenue);
    if (!Number.isFinite(rev) || rev <= 0) continue;
    const d = new Date(String(p.date) + "T12:00:00");
    const dow = d.getDay();
    const base = baseDow[dow];
    if (!base || base <= 0) continue;
    xs.push(i);          // giorni dall'inizio
    ys.push(rev / base); // residuo relativo (centrato su 1.0)
  }
  const nUsed = xs.length;
  if (nUsed < 28) {
    return {
      slopeWeekly: 0, slopeDaily: 0, n: series.length, nUsed,
      rSquared: 0, baseDow, valid: false,
      reason: `nUsed=${nUsed} < 28 dopo filtro (giorni con revenue=0 esclusi)`,
    };
  }
  // 3. OLS sui residui
  const meanX = xs.reduce((s, v) => s + v, 0) / nUsed;
  const meanY = ys.reduce((s, v) => s + v, 0) / nUsed;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < nUsed; i++) {
    const dx = xs[i] - meanX, dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  if (denX <= 0 || denY <= 0) {
    return {
      slopeWeekly: 0, slopeDaily: 0, n: series.length, nUsed,
      rSquared: 0, baseDow, valid: false,
      reason: "varianza nulla nei residui (serie costante post-detrend)",
    };
  }
  const slopeDaily = num / denX;             // slope in unità di "residuo per giorno"
  const slopeWeekly = slopeDaily * 7;        // approx lineare a 7gg
  const rSquared = (num * num) / (denX * denY);
  return {
    slopeWeekly, slopeDaily,
    n: series.length, nUsed,
    rSquared,
    baseDow,
    valid: true,
  };
}
// test: serie con sola stagionalità (ripete 7gg per 5 settimane) → slopeWeekly ≈ 0
// test: serie con trend +5%/sett + stagionalità → slopeWeekly ≈ 0.05

// ─── Esempio integrazione in engine.stats ────────────────────────────────────
//
// In src/App.jsx (engine.stats.*) si possono importare queste primitive così:
//
//   import * as stats from "./lib/stats.js";
//
//   // engine.stats.priceChangeAB(state, dishId, changeDate)
//   priceChangeAB(state, dishId, changeDate) {
//     const before = []; const after = [];
//     // ... popola per weekday matching ...
//     const test = stats.pairedTTest(before, after);
//     const nRequired = stats.sampleSize(2.0 /* Δ vendite/gg */, sd, 0.05, 0.80);
//     return { ...test, nRequired, currentN: before.length };
//   }
//
// Le primitive sono pure → testabili con vitest senza mock dello state.
